import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { fmtINR } from "@/lib/leadUtils";

/**
 * Phase 7.0 — Project P&L section restyle.
 * Lives inside the 440px-wide Accounts payment slideover, so the layout is
 * intentionally compact and single-column (no side-by-side label/value pairs
 * that would clip on narrow drawers).
 * VISUAL-ONLY: every data-testid, `api.get(/projects/{id}/pnl)`, and the
 * profit-sign branching is preserved.
 */
export default function ProjectPnlSection({ projectId }) {
  const [pnl, setPnl] = useState(null);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/projects/${projectId}/pnl`);
        setPnl(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  if (loading) {
    return (
      <div
        data-testid="project-pnl-loading"
        style={{
          fontSize: 10.5, fontWeight: 600, letterSpacing: "0.16em",
          textTransform: "uppercase", color: "#A39A8B",
        }}
      >
        P&L loading…
      </div>
    );
  }
  if (!pnl) return null;

  const profitColor = pnl.profit >= 0 ? "#3F6B51" : "#A35C45";

  return (
    <div data-testid="project-pnl-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="project-pnl-toggle"
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          width: "100%", marginBottom: 8, padding: 0,
          background: "transparent", border: "none", cursor: "pointer",
        }}
      >
        <span
          style={{
            fontSize: 10.5, fontWeight: 600, letterSpacing: "0.16em",
            textTransform: "uppercase", color: "#A39A8B",
          }}
        >
          P&L · <span style={{ color: profitColor, fontWeight: 700 }}>{fmtINR(pnl.profit)}</span>
        </span>
        {open
          ? <ChevronDown size={14} color="#A39A8B" />
          : <ChevronRight size={14} color="#A39A8B" />}
      </button>

      {open && (
        <div
          data-testid="project-pnl-body"
          style={{
            background: "#FFFFFF",
            border: "1px solid #E9E2D6",
            borderRadius: 8,
            padding: "10px 12px",
            boxShadow: "0 2px 6px rgba(60,46,20,.04)",
          }}
        >
          <Row label="Collected" value={fmtINR(pnl.revenue_collected)} positive />
          <Row label="− Vendor payouts" value={fmtINR(pnl.vendor_payouts_total)} negative />
          <Row label="− Editor payouts" value={fmtINR(pnl.editor_payouts_total)} negative />
          <Row label="− Project expenses" value={fmtINR(pnl.project_direct_expenses_total)} negative />
          <div
            style={{
              borderTop: "1px solid #EDE5D8",
              marginTop: 8, paddingTop: 8,
              display: "flex", justifyContent: "space-between", alignItems: "center",
            }}
          >
            <span
              style={{
                fontSize: 10.5, fontWeight: 700, letterSpacing: "0.16em",
                textTransform: "uppercase", color: "#22382C",
              }}
            >
              Profit
            </span>
            <div style={{ textAlign: "right" }}>
              <div
                data-testid="project-pnl-profit"
                style={{ fontSize: 17, fontWeight: 600, color: profitColor, lineHeight: 1.1 }}
              >
                {fmtINR(pnl.profit)}
              </div>
              {pnl.margin_pct !== null && (
                <div
                  data-testid="project-pnl-margin"
                  style={{ fontSize: 10, color: "#A39A8B", marginTop: 2 }}
                >
                  {pnl.margin_pct.toFixed(1)}% margin
                </div>
              )}
            </div>
          </div>
          <p
            style={{
              fontSize: 10, color: "#A39A8B", marginTop: 10,
              fontStyle: "italic", lineHeight: 1.4,
            }}
          >
            Direct costs only. Studio overhead is excluded by design.
          </p>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, positive, negative }) {
  return (
    <div
      data-testid={`pnl-row-${label.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}
      style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "4px 0",
      }}
    >
      <span style={{ fontSize: 12, color: "#76705F" }}>{label}</span>
      <span
        style={{
          fontSize: 13, fontWeight: 500,
          color: positive ? "#3F6B51" : negative ? "#A35C45" : "#2A2722",
        }}
      >
        {value}
      </span>
    </div>
  );
}
